// @ts-nocheck: converted from JS

import ApiErrorBanner from '@/components/ApiErrorBanner';
import Loading from '@/components/Loading';
import { GroupsAndTreatmentsCreation } from '@/components/Studies/Create/Steps/GroupsAndTreatments/GroupsAndTreatmentsCreation';
import { TaskVersionSelector } from '@/components/Studies/Create/Steps/Tasks/TaskVersionSelector';
import Banner from '@/components/UI/Banner';
import FormRender from '@/components/UI/FormRender';
import Header from '@/components/UI/Header';
import SubHeader from '@/components/UI/SubHeader';
import { Panel, Tab, Tabs } from '@/components/UI/Tabs';
import { successToast } from '@/helpers';
import { BENCHLING_INTEGRATION_META_SLUG } from '@/helpers.constants.tsx';
import { _isNotEmpty, _notNil, safelyDecodeURIComponent } from '@/littledash';
import type { ID } from '@/model/Common.model';
import type { GroupsTreatments, StudyDraft } from '@/model/Draft.model';
import type { PlannedAnimalData } from '@/model/PlannedAnimal.model';
import InVivoError from '@/model/InVivoError.ts';
import type {
  BuildSummaryStepProps,
  StudyFormMetadataItem,
  StudyFormMetaDetails,
  StudyFormSection,
} from '@/model/StudyForm.model';
import { Treatment } from '@/model/Treatment.model.ts';
import type { TreatmentGroup } from '@/model/TreatmentGroup.model';
import { ApiService } from '@/support/ApiService';
import Http from '@/support/http';
import { api as apiRoute, web as webRoute } from '@/support/route';
import { currentUserSelector } from '@/support/Selectors.tsx';
import { DateUtils } from '@/utils/Date.utils.ts';
import { ExceptionHandler } from '@/utils/ExceptionHandler';
import { createSelector } from '@reduxjs/toolkit';
import { FC, useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';
//@ts-expect-error - old hook form - replace with `react-hook-form@latest`
import { FormProvider, useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { CreateStudyReducer, CreateStudyState } from './Create.model';
import { studyCreateSectionSlug } from '@/model/StudyForm.model.ts';
import {
  assemblePayload,
  clearInvalidMetadataFromState,
  FormActions,
  initialiseState,
  PanelLayout,
  reducer,
  UseFormProvider,
  UseLatestFormProvider,
  validateAndClearInvalidMetadata,
} from './Show.utils';
import Alerts from './Steps/Alerts';
import Attachments from './Steps/Attachments';
import BenchlingIntegration from './Steps/BenchlingIntegration';
import Information from './Steps/Information';
import SelectPreset from './Steps/SelectPreset';
import Summary from './Steps/Summary';
import Team from './Steps/Team';
import withDraft from './withDraft';
import { useStudyCreateGroups, useStudyCreateSave, useStudyCreateStep } from '@/utils/studyCreation/useStudyCreate.tsx';
import Animals from '@/components/Studies/Create/Steps/Animals/Animals.tsx';
import Cages from '@/components/Studies/Create/Steps/Cages';
import { SamplingPlanStep } from '@/components/Studies/Create/Steps/SamplingPlan/SamplingPlanStep';

interface CustomSectionComponentProps {
  state: CreateStudyState;
  dispatch: () => void;
  totalSteps: number;
  formId: ID;
  formMeta: StudyFormMetaDetails | undefined;
}

interface StudyStep {
  name: string;
  slug: StudyCreateSectionSlug;
  Component: (props: CustomSectionComponentProps) => JSX.Element;
  formMeta?: StudyFormMetaDetails;
}

const INFORMATION_STEP: StudyStep = {
  name: 'Information',
  Component: ({ state, dispatch }) => (
    <UseLatestFormProvider state={state} dispatch={dispatch}>
      <Information dispatch={dispatch} />
    </UseLatestFormProvider>
  ),
};

const TEAM_STEP: StudyStep = {
  name: 'Team',
  Component: ({ state, dispatch }) => {
    const {
      study: { users, owner, author },
    } = state;
    return (
      <UseFormProvider state={state} dispatch={dispatch}>
        <Team users={users} owner={owner} author={author} />
      </UseFormProvider>
    );
  },
};

const requiredSteps = [INFORMATION_STEP, TEAM_STEP];

const buildSummaryStep: (summaryProps: BuildSummaryStepProps) => StudyStep = ({ handleSubmit, formMeta }) => ({
  name: 'Summary',
  Component: ({ state, dispatch }) => (
    <>
      <div className="mw8 mb4 flex justify-between flex-wrap items-top" data-testid="summary-title__container">
        <div>
          <h3 className="normal f4 lh-title pb2">Summary</h3>
          <p className="lh-copy f5">Confirm your study settings are correct before continuing.</p>
        </div>
        <FormActions state={state} dispatch={dispatch} submitForm={handleSubmit} disableSaveDraft />
      </div>
      <Summary state={state} dispatch={dispatch} formMeta={formMeta} />
      <FormActions
        state={state}
        dispatch={dispatch}
        submitForm={handleSubmit}
        disableSaveDraft
        testKey="summary-submit"
      />
    </>
  ),
});

const buildBenchlingIntegrationStep = (metaglossary: StudyFormMetadataItem) => ({
  name: 'Benchling Integration',
  Component: ({ state, dispatch }) => (
    <UseLatestFormProvider state={state} dispatch={dispatch}>
      <BenchlingIntegration state={state} dispatch={dispatch} metaglossary={metaglossary} />
    </UseLatestFormProvider>
  ),
});

interface ShowProps {
  draft: StudyDraft;
}

const selector = createSelector(
  [currentUserSelector],
  (currentUser) => currentUser?.timezone ?? DateUtils.browserTimezone()
);
const Show: FC<ShowProps> = ({ draft }) => {
  const userTimezone = useSelector(selector);
  const initialState = useMemo(() => initialiseState(draft, userTimezone), [draft, userTimezone]);
  const [state, dispatch] = useReducer<CreateStudyReducer>(reducer, initialState);
  const [formMeta, setFormMeta] = useState<StudyFormMetaDetails>();
  const [formMetaLoading, setFormMetaLoading] = useState<boolean>(true);
  const navigate = useNavigate();
  const location = useLocation();
  const formId = new URLSearchParams(location.search).get('formId') as ID;
  const { currentStep, setStepReady } = useStudyCreateStep();
  const { savingDraft, advancingStep, setSavingDraft, saveAndAdvance, loadDraftData, draftId, setDraftId } =
    useStudyCreateSave();
  const { groups, treatments, groupsTreatments, plannedAnimalData } = useStudyCreateGroups();

  const {
    benchling_folders: benchlingFoldersEnabled,
    planned_animals: plannedAnimalsEnabled,
    planned_samples: plannedSamplesEnabled,
  } = useSelector(
    ({
      team: {
        features: { benchling_folders, planned_animals, planned_samples },
      },
    }: State) => ({
      benchling_folders,
      planned_animals,
      planned_samples,
    })
  );

  const STANDARD_STEPS: Array<StudyStep> = [];

  STANDARD_STEPS.push(
    {
      name: 'Cages & Animals',
      slug: studyCreateSectionSlug.cages_animals,
      Component: ({ state, dispatch }) => (
        <UseFormProvider state={state} dispatch={dispatch}>
          <Cages state={state} dispatch={dispatch} />
        </UseFormProvider>
      ),
    },
    {
      name: 'Groups & Treatments',
      slug: studyCreateSectionSlug.groups,
      Component: ({ state, dispatch }) => (
        <>
          <GroupsAndTreatmentsCreation state={state} dispatch={dispatch} />
          <FormActions state={state} dispatch={dispatch} />
        </>
      ),
    }
  );

  if (plannedAnimalsEnabled === true) {
    STANDARD_STEPS.push({
      name: 'Animals',
      slug: studyCreateSectionSlug.animals,
      Component: ({ state, dispatch }) => (
        <UseFormProvider state={state} dispatch={dispatch}>
          <Animals state={state} dispatch={dispatch} />
        </UseFormProvider>
      ),
    });
  }
  if (plannedSamplesEnabled === true) {
    STANDARD_STEPS.push({
      name: 'Sampling plan',
      slug: studyCreateSectionSlug.sampling_plan,
      Component: ({ state, dispatch }) => <SamplingPlanStep state={state} dispatch={dispatch} />,
    });
  }
  STANDARD_STEPS.push(
    {
      name: 'Select a preset',
      slug: studyCreateSectionSlug.presets,
      Component: ({ state, dispatch }) => (
        <>
          <SelectPreset state={state} dispatch={dispatch} />
          <FormActions state={state} dispatch={dispatch} />
        </>
      ),
    },
    {
      name: 'Alerts',
      slug: studyCreateSectionSlug.alerts,
      Component: ({ state, dispatch }) => (
        <UseFormProvider state={state} dispatch={dispatch}>
          <Alerts state={state} />
        </UseFormProvider>
      ),
    },
    {
      name: 'Tasks',
      slug: studyCreateSectionSlug.tasks,
      Component: ({ state, dispatch }) => (
        <>
          <TaskVersionSelector state={state} dispatch={dispatch} />
          <FormActions state={state} dispatch={dispatch} />
        </>
      ),
    },
    {
      name: 'Attachments',
      slug: studyCreateSectionSlug.attachments,
      Component: ({ state, dispatch, formId, formMeta }) => (
        <>
          <Attachments state={state} dispatch={dispatch} formId={formId} formMeta={formMeta} />
          <FormActions state={state} dispatch={dispatch} />
        </>
      ),
    }
  );

  const fetchStudyForm = async (formId: ID) => {
    setFormMetaLoading(true);
    try {
      const {
        data: { data: formTemplate },
      } = await Http.get(
        apiRoute('team.studies.forms.get', {
          formId,
        })
      );
      setFormMeta(formTemplate);
    } catch (e) {
      dispatch({ type: 'updateForm', data: { errors: e } });
    } finally {
      setFormMetaLoading(false);
    }
  };

  const fetchFormRequirements = async () => {
    try {
      const metadataResponse = await Http.get(apiRoute('meta-glossary.show'), {
        params: {
          filter_type: 'study_meta',
        },
      });
      const taskTemplateResponse = await Http.get(apiRoute('tasks.templateGroups.show'));
      const presetResponse = await Http.get(apiRoute('study.presets'));
      dispatch({
        type: 'SET_SELECTABLE_METADATA',
        data: metadataResponse.data.data,
      });
      dispatch({
        type: 'updateForm',
        data: {
          templates: taskTemplateResponse.data.data,
          presets: presetResponse.data.data,
        },
      });
    } catch (errors) {
      dispatch({ type: 'updateForm', data: { errors } });
      setStepReady(true);
    } finally {
      dispatch({ type: 'updateForm', data: { loading: false } });
    }
  };

  const handleSubmit = useCallback(
    async (
      innerState,
      groups: Array<TreatmentGroup>,
      treatments: Array<Treatment>,
      groupsTreatments: Array<GroupsTreatments>,
      plannedAnimalData: PlannedAnimalData
    ) => {
      dispatch({ type: 'updateForm', data: { submitting: true } });
      const payload = assemblePayload(
        innerState.study,
        groups,
        treatments,
        groupsTreatments,
        plannedAnimalData,
        formMeta,
        formId
      );
      if (_notNil(draftId)) {
        payload.draft_id = draftId;
      }

      const v2Tasks = innerState.study.tasks ?? {};
      if (_isNotEmpty(Object.values(v2Tasks))) {
        payload.tasks = v2Tasks;
      }

      try {
        const response = await ApiService.call({
          endpoint: 'POST /api/v1/studies',
          body: payload,
          options: { onError: { toast: false, capture: false, slug: 'study-create' } },
        });
        const data = response.body.data;

        dispatch({
          type: 'updateForm',
          data: { result: data, submitting: false, submitted: true },
        });
        navigate(webRoute('studies.show', { id: data.id }));
        successToast('Study created successfully');
      } catch (errors) {
        ExceptionHandler.captureException(
          new InVivoError('Could not create study', {
            cause: errors,
            slug: 'study-create',
          })
        );
        dispatch({
          type: 'updateForm',
          data: { errors, submitting: false },
        });
      }
    },
    [dispatch, formMeta, formId, draftId, history]
  );

  const handleSaveDraft = useCallback(async () => {
    const { study } = state;
    try {
      const payload = {
        name: study.name,
        form_data: assemblePayload(study, groups, treatments, groupsTreatments, plannedAnimalData, formMeta, formId),
        config: {
          last_section_id: currentStep,
          form_id: formId,
        },
      };
      if (_notNil(draftId)) {
        await ApiService.call({
          endpoint: 'PATCH /api/v1/drafts/studies/{draftId}',
          body: payload,
          path: { draftId },
        });
        if (advancingStep) {
          saveAndAdvance({ type: 'COMPLETE' });
        } else {
          setSavingDraft(false);
          successToast('Study draft saved successfully');
        }
      } else {
        const response = await ApiService.call({
          endpoint: 'POST /api/v1/drafts/studies',
          body: payload,
        });

        if (response.type === 'success') {
          const newDraftId = response.body.id;
          setDraftId(newDraftId);

          if (advancingStep) {
            saveAndAdvance({ type: 'COMPLETE' });
          } else {
            setSavingDraft(false);
            successToast('Study draft saved successfully');
          }
        }
      }
    } catch (errors) {
      dispatch({
        type: 'updateForm',
        data: { errors },
      });
      setSavingDraft(false);
      ExceptionHandler.captureException(
        new InVivoError('Could not save draft', {
          cause: errors,
          slug: 'save-draft',
        })
      );
    }
  }, [
    state,
    formMeta,
    formId,
    currentStep,
    advancingStep,
    setSavingDraft,
    saveAndAdvance,
    groups,
    treatments,
    groupsTreatments,
    plannedAnimalData,
    draftId,
    setDraftId,
  ]);

  const defaultProps = { state, dispatch };

  const confirmNavigation = (e: BeforeUnloadEvent) => {
    const message = 'Are you sure you want to leave? Data you have entered may not be saved.';

    if (!state.form.submitted) {
      (e ?? window.event).returnValue = message;
      return message;
    } else {
      return false;
    }
  };

  useEffect(() => {
    fetchFormRequirements();
  }, []);

  useEffect(() => {
    if (_notNil(draft?.config?.form_id) || _notNil(formId)) {
      fetchStudyForm(draft?.config?.form_id ?? formId);
    }
  }, [formId, draft]);

  useEffect(() => {
    window.addEventListener('beforeunload', confirmNavigation);
    return () => window.removeEventListener('beforeunload', confirmNavigation);
  }, [state.form.submitted]);

  useEffect(() => {
    if (savingDraft) {
      handleSaveDraft();
    }
  }, [savingDraft, handleSaveDraft]);

  useEffect(() => {
    if (_notNil(draft?.form_data)) {
      loadDraftData(draft.form_data);
    }
  }, [draft, loadDraftData]);

  interface CustomFormRenderField {
    name: string;
    type: string;
    label: string;
    required: boolean;
    metadata: {
      id: ID;
    };
    options?: Array<{
      label: string;
      value: string;
    }>;
  }

  const buildStudyFormMetadataField = (metadataItem: StudyFormMetadataItem) => {
    const field: CustomFormRenderField = {
      name: `metadata[${metadataItem.info.id}]`,
      type: metadataItem.info.field_type,
      label: metadataItem.info.title,
      required: Boolean(metadataItem.required),
      metadata: {
        id: metadataItem.info.id,
      },
      slug: metadataItem.info.slug,
    };
    if (_notNil(metadataItem.info.options)) {
      field.options = metadataItem.info.options.map((option) => ({
        label: safelyDecodeURIComponent(option),
        value: option,
      }));
    }
    return field;
  };

  const mapFormSectionToFormRenderFields = (section: StudyFormSection) =>
    Object.values(section.metadata).reduce((acc, metadataItem) => {
      if (benchlingFoldersEnabled === true) {
        if (metadataItem.info.slug !== BENCHLING_INTEGRATION_META_SLUG) {
          acc.push(buildStudyFormMetadataField(metadataItem));
        }
      } else {
        acc.push(buildStudyFormMetadataField(metadataItem));
      }
      return acc;
    }, []);

  const buildSteps: () => Array<StudyStep> = () => {
    if (_notNil(formMeta)) {
      const steps = [...requiredSteps];
      formMeta.sections.forEach((section) => {
        if (section.active) {
          if (section.type === 'standard') {
            const step = STANDARD_STEPS.find((standardStep) => standardStep.slug === section.slug);
            if (_notNil(step)) {
              steps.push(step);
            }
          } else if (section.type === 'custom') {
            const metaFields = section.metadata?.reduce((acc, metaField) => {
              if (benchlingFoldersEnabled === true) {
                if (metaField?.info?.slug !== BENCHLING_INTEGRATION_META_SLUG) {
                  acc.push(metaField);
                }
              } else {
                acc.push(metaField);
              }
              return acc;
            }, []);
            if (_isNotEmpty(metaFields)) {
              steps.push({
                name: section.name,
                Component: ({ state, dispatch }) => {
                  const hasClearedRef = useRef(false);

                  const { invalidFields, clearedFormData, hasInvalidFields } = useMemo(
                    () => validateAndClearInvalidMetadata(section, state),
                    []
                  );

                  const formMethods = useForm({
                    defaultValues: {
                      metadata: clearedFormData,
                    },
                  });

                  useEffect(() => {
                    if (hasInvalidFields && !hasClearedRef.current) {
                      clearInvalidMetadataFromState(Object.keys(invalidFields), dispatch);
                      hasClearedRef.current = true;
                    }
                  }, [hasInvalidFields, invalidFields, dispatch]);

                  return (
                    <FormProvider {...formMethods}>
                      {hasInvalidFields && (
                        <Banner warning className="mw7 mb4 black" dismiss={true}>
                          <>
                            <h3 className="db mb1">Invalid metadata values</h3>
                            <p className="ma0 lh-copy">
                              The following fields had values that are no longer valid:{' '}
                              {Object.values(invalidFields).join(', ')}. Select valid options for these fields before
                              continuing to the next step.
                            </p>
                          </>
                        </Banner>
                      )}
                      <PanelLayout title={section.name}>
                        <FormRender fields={mapFormSectionToFormRenderFields(section)} studyCreate={true} />
                      </PanelLayout>
                      <FormActions state={state} dispatch={dispatch} isUseForm submitAction="updateMetadata" />
                    </FormProvider>
                  );
                },
              });
            }
            const benchlingFolderMetaGlossary = section.metadata.find(
              (meta) => meta.info.slug === BENCHLING_INTEGRATION_META_SLUG
            );
            if (benchlingFoldersEnabled === true && _notNil(benchlingFolderMetaGlossary)) {
              steps.push(buildBenchlingIntegrationStep(benchlingFolderMetaGlossary));
            }
          }
        }
      });
      steps.push(buildSummaryStep({ handleSubmit, formMeta }));
      return steps;
    }
    return [];
  };

  const steps = useMemo(() => buildSteps(), [formMeta, handleSubmit]);

  return (
    <div className="h-100">
      <SubHeader linkToText="Studies" link={webRoute('dashboard')} />
      {state.form.loading || formMetaLoading ? (
        <div className="w-100 h-100">
          <Loading txt="Fetching team settings..." />
        </div>
      ) : (
        <div className="h-100">
          {!state.form.submitting ? (
            <>
              {_isNotEmpty(state.form.errors) && (
                <ApiErrorBanner
                  title="There was a problem with your submission"
                  text="We've logged this error. If this problem keeps occurring, please contact support."
                  error={state.form.errors}
                  className="ma4"
                />
              )}
              <div className="ph4 pv3">
                <Header mainHeaderText="Create a new study" />
              </div>
              <div className="ph3 ph4-l mb4">
                <RepurposedStepForm formMeta={formMeta} formId={formId} steps={steps} panelProps={defaultProps} />
              </div>
            </>
          ) : (
            <div className="w-100 h-100">
              <Loading txt="Creating a new study..." />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const RepurposedStepForm = ({ formMeta, formId, steps, panelProps }) => {
  const { currentStep } = useStudyCreateStep();
  return (
    <Tabs state={currentStep}>
      <div className="flex flex-wrap justify-between">
        <div className="w-20">
          {steps.map((s, index) => {
            return (
              <Tab
                plain
                key={index}
                className={`mb3 ${index === currentStep ? 'near-black' : 'dark-gray'}`}
                disableNavigation
                testId={`step-tab-${s.name}`}
              >
                {s.name}
              </Tab>
            );
          })}
        </div>
        <div className="w-80 mb5">
          {steps.map((step, i) => (
            <Panel key={i}>
              <step.Component {...panelProps} totalSteps={steps.length} formMeta={formMeta} formId={formId} />
            </Panel>
          ))}
        </div>
      </div>
    </Tabs>
  );
};

export default withDraft(Show);
